// src/modules/smelter/components/TransitMemoModal.jsx
// Raises the "Gold Transit Memo" (AGTM) that travels with the metal: the employees
// carrying it, an editable incremental memo number, the destination smelter and the
// director signatories. Used from Create Dispatch, Dispatch Details and the memo list.

import React, { useEffect, useState } from 'react';
import {
  Modal, Form, Input, InputNumber, Select, DatePicker, Row, Col, Button,
  Divider, Typography, Space, message,
} from 'antd';
import { PlusOutlined, DeleteOutlined, DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchSmelters, fetchMemoEmployees, fetchMemoDefaults,
  createTransitMemo, updateTransitMemo, downloadTransitMemoPdf, previewTransitMemoPdf,
} from '../api';
import { METAL_OPTIONS } from '../constants';

const { Text } = Typography;
const { TextArea } = Input;

const ID_TYPE_OPTIONS = [
  { value: 'AADHAAR', label: 'Aadhaar' },
  { value: 'PAN', label: 'PAN' },
  { value: 'DL', label: 'Driving Licence' },
  { value: 'OTHER', label: 'Other' },
];

const MODE_OPTIONS = [
  { value: 'TRAIN', label: 'Train' },
  { value: 'BUS', label: 'Bus' },
  { value: 'METRO', label: 'Metro' },
  { value: 'OTHER', label: 'Other' },
];

// A memo can tick several modes; memos saved before that carry a single
// `modeOfTransport`, so read either shape.
const toModes = (src = {}) => {
  const modes = (src.modesOfTransport || [src.modeOfTransport])
    .filter((m) => MODE_OPTIONS.some((o) => o.value === m));
  return modes.length ? modes : ['TRAIN'];
};

const round4 = (n) => Math.round((Number(n) || 0) * 10000) / 10000;
const metalWord = (metalType) => (String(metalType).toUpperCase() === 'SILVER' ? 'silver' : 'gold');

// "Vicky Jewellery Works Pvt. Ltd., 202D, Bipin Behari Ganguly Street, Kolkata - 700012"
const destinationLine = (dest = {}) =>
  [dest.name, dest.address].map((v) => (v || '').trim()).filter(Boolean).join(', ');

const emptyItem = (metalType) => ({
  description: `Melted ${metalWord(metalType) === 'silver' ? 'Silver' : 'Gold'} Jwellery`,
  itemCount: 1,
  grossWeight: 0,
  remarks: 'Company Stock',
});

export default function TransitMemoModal({
  open,
  onClose,
  onSaved,
  memo = null,          // existing memo → edit mode
  prefill = {},         // { smelterId, metalType, dispatchId, dispatchNumber, inventoryBatchId, batchNumber, items, totalWeight }
}) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [smelters, setSmelters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState({ purposeTemplate: '', declarationTemplate: '', originAddress: '', seller: {} });
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  // Once the user edits the wording by hand we stop regenerating it underneath them.
  const [textTouched, setTextTouched] = useState({ purpose: false, declaration: false });

  const isEdit = !!memo?._id;

  // ── Load reference data + seed the form ───────────────────────
  useEffect(() => {
    if (!open) return;
    setTextTouched({ purpose: isEdit, declaration: isEdit });

    Promise.all([
      fetchSmelters({ active: true }).catch(() => ({ data: [] })),
      fetchMemoEmployees().catch(() => ({ data: [] })),
      fetchMemoDefaults(dayjs().toISOString()).catch(() => ({ data: null })),
    ]).then(([smelterRes, empRes, defRes]) => {
      const smelterList = smelterRes.data || [];
      setSmelters(smelterList);
      setEmployees(empRes.data || []);
      const defaults = defRes.data || {};
      setTemplates({
        purposeTemplate: defaults.purposeTemplate || '',
        declarationTemplate: defaults.declarationTemplate || '',
        originAddress: defaults.originAddress || '',
        seller: defaults.seller || {},
      });

      if (isEdit) {
        form.setFieldsValue({
          ...memo,
          memoDate: memo.memoDate ? dayjs(memo.memoDate) : dayjs(),
          modesOfTransport: toModes(memo),
          representativeUserIds: (memo.representatives || []).map((r) => r.userId).filter(Boolean),
        });
        return;
      }

      const metalType = prefill.metalType || 'GOLD';
      const smelter = smelterList.find((s) => s._id === prefill.smelterId) || null;
      const items = (prefill.items || []).length ? prefill.items : [emptyItem(metalType)];
      form.setFieldsValue({
        memoNo: defaults.memoNo || '',
        memoDate: dayjs(),
        metalType,
        smelterId: prefill.smelterId || undefined,
        dispatchId: prefill.dispatchId || null,
        dispatchNumber: prefill.dispatchNumber || '',
        inventoryBatchId: prefill.inventoryBatchId || null,
        batchNumber: prefill.batchNumber || '',
        representativeUserIds: [],
        representatives: [],
        items,
        totalWeight: prefill.totalWeight != null
          ? round4(prefill.totalWeight)
          : round4(items.reduce((s, it) => s + (Number(it.grossWeight) || 0), 0)),
        placeOfDispatch: defaults.placeOfDispatch || { name: '', address: '' },
        destination: smelter
          ? { name: smelter.name, address: smelter.address, city: '', state: smelter.state, gstin: smelter.gstin }
          : { name: '', address: '', city: '', state: '', gstin: '' },
        modesOfTransport: toModes(defaults),
        modeOther: '',
        directors: (defaults.directors || []).length
          ? defaults.directors
          : [{ name: '', din: '', designation: 'Authorized Director' }],
        notes: '',
      });
      applyTemplates({ force: true, defaults });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, memo?._id]);

  // Re-render the purpose / declaration wording from the current destination.
  const applyTemplates = ({ force = false, defaults = null } = {}) => {
    const t = defaults
      ? { purposeTemplate: defaults.purposeTemplate, declarationTemplate: defaults.declarationTemplate, originAddress: defaults.originAddress, seller: defaults.seller || {} }
      : templates;
    if (!t.purposeTemplate && !t.declarationTemplate) return;

    const vals = form.getFieldsValue();
    const metal = metalWord(vals.metalType);
    const origin = (vals.placeOfDispatch?.address || t.originAddress || '').trim();
    const dest = destinationLine(vals.destination) || '—';
    const company = (t.seller?.name || '').trim();

    const next = {};
    if (force || !textTouched.purpose) {
      next.purpose = (t.purposeTemplate || '')
        .replace(/\{metal\}/g, metal)
        .replace(/\{origin\}/g, origin)
        .replace(/\{destination\}/g, dest);
    }
    if (force || !textTouched.declaration) {
      next.declaration = (t.declarationTemplate || '')
        .replace(/\{metal\}/g, metal)
        .replace(/\{company\}/g, company);
    }
    form.setFieldsValue(next);
  };

  // ── Smelter → destination snapshot ────────────────────────────
  const onSmelterChange = (smelterId) => {
    const s = smelters.find((x) => x._id === smelterId);
    if (s) {
      form.setFieldsValue({
        destination: {
          name: s.name || '',
          address: s.address || '',
          city: form.getFieldValue(['destination', 'city']) || '',
          state: s.state || '',
          gstin: s.gstin || '',
        },
      });
    }
    applyTemplates();
  };

  // ── Employee picker → representative rows ─────────────────────
  // Rows the user typed by hand (no userId) always survive; employee rows follow
  // the selection, keeping any edits already made to a still-selected employee.
  const onEmployeesChange = (userIds) => {
    const current = form.getFieldValue('representatives') || [];
    const manual = current.filter((r) => r && !r.userId);
    const kept = userIds.map((id) => {
      const existing = current.find((r) => r && r.userId === id);
      if (existing) return existing;
      const emp = employees.find((e) => e._id === id) || {};
      return {
        userId: id,
        name: emp.name || '',
        designation: emp.designation || '',
        idType: 'AADHAAR',
        idNo: emp.adhar || '',
        employeeCode: emp.employeeCode || '',
      };
    });
    form.setFieldsValue({ representatives: [...kept, ...manual] });
  };

  // Switching the ID type re-reads the matching number off the employee record.
  const onIdTypeChange = (index, idType) => {
    const reps = [...(form.getFieldValue('representatives') || [])];
    const row = reps[index];
    if (!row?.userId) return;
    const emp = employees.find((e) => e._id === row.userId) || {};
    const idNo = idType === 'AADHAAR' ? (emp.adhar || '') : (idType === 'PAN' ? (emp.pan || '') : '');
    reps[index] = { ...row, idType, idNo };
    form.setFieldsValue({ representatives: reps });
  };

  const syncTotalWeight = () => {
    const items = form.getFieldValue('items') || [];
    form.setFieldsValue({
      totalWeight: round4(items.reduce((s, it) => s + (Number(it?.grossWeight) || 0), 0)),
    });
  };

  const buildPayload = (values) => ({
    memoNo: (values.memoNo || '').trim(),
    memoDate: (values.memoDate || dayjs()).toISOString(),
    metalType: values.metalType,
    smelterId: values.smelterId,
    dispatchId: values.dispatchId || null,
    dispatchNumber: values.dispatchNumber || '',
    inventoryBatchId: values.inventoryBatchId || null,
    batchNumber: values.batchNumber || '',
    representatives: (values.representatives || []).filter((r) => r && (r.name || '').trim()),
    items: (values.items || []).filter((it) => it && ((it.description || '').trim() || Number(it.grossWeight) > 0)),
    totalWeight: values.totalWeight,
    placeOfDispatch: values.placeOfDispatch,
    destination: values.destination,
    modesOfTransport: (values.modesOfTransport || []).length ? values.modesOfTransport : ['TRAIN'],
    modeOther: values.modeOther || '',
    purpose: values.purpose,
    declaration: values.declaration,
    notes: values.notes || '',
    directors: (values.directors || []).filter((d) => d && ((d.name || '').trim() || (d.din || '').trim())),
  });

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const values = await form.validateFields();
      await previewTransitMemoPdf(buildPayload(values));
    } catch (e) {
      if (e?.errorFields) messageApi.error('Fill the highlighted fields first');
      else messageApi.error(e.response?.data?.message || 'Failed to render the memo');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);
      const res = isEdit
        ? await updateTransitMemo(memo._id, payload)
        : await createTransitMemo(payload);
      const saved = res.data;
      messageApi.success(`Transit memo ${saved.memoNo} saved`);
      try {
        await downloadTransitMemoPdf(saved._id, saved.memoNo);
      } catch (_) {
        messageApi.warning('Memo saved, but the PDF could not be downloaded automatically.');
      }
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      if (e?.errorFields) messageApi.error('Fill the highlighted fields first');
      else messageApi.error(e.response?.data?.message || 'Failed to save the transit memo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={isEdit ? `Edit Transit Memo — ${memo.memoNo}` : 'Gold Transit Memo'}
      width={980}
      destroyOnClose
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="preview" icon={<FilePdfOutlined />} loading={previewing} onClick={handlePreview}>
          Preview PDF
        </Button>,
        <Button key="save" type="primary" icon={<DownloadOutlined />} loading={saving} onClick={handleSave}>
          {isEdit ? 'Save & Download' : 'Save & Download Memo'}
        </Button>,
      ]}
    >
      {contextHolder}
      <Form form={form} layout="vertical" size="small">
        <Form.Item name="dispatchId" hidden><Input /></Form.Item>
        <Form.Item name="dispatchNumber" hidden><Input /></Form.Item>
        <Form.Item name="inventoryBatchId" hidden><Input /></Form.Item>
        <Form.Item name="batchNumber" hidden><Input /></Form.Item>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Memo No." name="memoNo"
              tooltip="Auto-incremented AGTM series (AGTM/<FY>/001). Editable — the series is advanced past whatever you type."
              rules={[{ required: true, message: 'Memo number is required' }]}
            >
              <Input placeholder="AGTM/2026/008" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item label="Date" name="memoDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item label="Metal" name="metalType">
              <Select options={METAL_OPTIONS} onChange={() => applyTemplates()} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ margin: '4px 0 12px', fontSize: 13 }}>
          Details of Authorized Representative
        </Divider>

        <Form.Item
          label="Employees Carrying the Consignment" name="representativeUserIds"
          tooltip="Picks up each employee's designation, Aadhaar and employee code — all still editable below."
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="Select employees…"
            optionFilterProp="label"
            onChange={onEmployeesChange}
            options={employees.map((e) => ({
              value: e._id,
              label: `${e.name}${e.employeeCode ? ` (${e.employeeCode})` : ''}${e.designation ? ` — ${e.designation}` : ''}`,
            }))}
          />
        </Form.Item>

        <Form.List name="representatives">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <Row key={field.key} gutter={8} align="middle" style={{ marginBottom: 4 }}>
                  <Form.Item name={[field.name, 'userId']} hidden><Input /></Form.Item>
                  <Col xs={24} md={5}>
                    <Form.Item name={[field.name, 'name']} rules={[{ required: true, message: 'Name required' }]}>
                      <Input placeholder="Name" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item name={[field.name, 'designation']}>
                      <Input placeholder="Designation" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Item name={[field.name, 'idType']}>
                      <Select options={ID_TYPE_OPTIONS} onChange={(v) => onIdTypeChange(field.name, v)} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={5}>
                    <Form.Item name={[field.name, 'idNo']}>
                      <Input placeholder="Official ID No." />
                    </Form.Item>
                  </Col>
                  <Col xs={10} md={3}>
                    <Form.Item name={[field.name, 'employeeCode']}>
                      <Input placeholder="Emp. ID" />
                    </Form.Item>
                  </Col>
                  <Col xs={2} md={1}>
                    <Button
                      type="text" danger icon={<DeleteOutlined />}
                      onClick={() => {
                        const row = (form.getFieldValue('representatives') || [])[field.name];
                        remove(field.name);
                        if (row?.userId) {
                          form.setFieldsValue({
                            representativeUserIds: (form.getFieldValue('representativeUserIds') || [])
                              .filter((id) => id !== row.userId),
                          });
                        }
                      }}
                    />
                  </Col>
                </Row>
              ))}
              <Button
                type="dashed" block icon={<PlusOutlined />} style={{ marginBottom: 12 }}
                onClick={() => add({ name: '', designation: '', idType: 'AADHAAR', idNo: '', employeeCode: '' })}
              >
                Add Representative (not on payroll)
              </Button>
            </>
          )}
        </Form.List>

        <Divider orientation="left" style={{ margin: '4px 0 12px', fontSize: 13 }}>Destination</Divider>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Smelter" name="smelterId" rules={[{ required: true, message: 'Select the smelter' }]}>
              <Select
                showSearch placeholder="Select smelter…" optionFilterProp="label"
                onChange={onSmelterChange}
                options={smelters.map((s) => ({ value: s._id, label: `${s.name}${s.contactPerson ? ` — ${s.contactPerson}` : ''}` }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Destination Name" name={['destination', 'name']} rules={[{ required: true, message: 'Required' }]}>
              <Input onChange={() => applyTemplates()} />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item label="City" name={['destination', 'city']}>
              <Input placeholder="Kolkata" />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item label="State" name={['destination', 'state']}>
              <Input placeholder="West Bengal" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Destination Address" name={['destination', 'address']}>
              <Input placeholder="202D, Bipin Behari Ganguly Street, Kolkata - 700012, West Bengal" onChange={() => applyTemplates()} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Place of Dispatch" name={['placeOfDispatch', 'name']}>
              <Input placeholder="Auriksha Edge Private Limited" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Dispatch From (Address)" name={['placeOfDispatch', 'address']}>
              <Input
                placeholder="C-11, Central Park, Doc's Colony, City Centre, Durgapur - 713216"
                onChange={() => applyTemplates()}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={5}>
            <Form.Item label="Mode of Transport" name="modesOfTransport">
              <Select
                mode="multiple"
                allowClear
                options={MODE_OPTIONS}
                placeholder="Train, Bus…"
                maxTagCount="responsive"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={3}>
            <Form.Item
              noStyle
              shouldUpdate={(p, c) => p.modesOfTransport !== c.modesOfTransport}
            >
              {({ getFieldValue }) => (
                <Form.Item label="Other (specify)" name="modeOther">
                  <Input
                    disabled={!(getFieldValue('modesOfTransport') || []).includes('OTHER')}
                    placeholder="Car / Courier"
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ margin: '4px 0 12px', fontSize: 13 }}>Details of Gold</Divider>

        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, idx) => (
                <Row key={field.key} gutter={8} align="middle" style={{ marginBottom: 4 }}>
                  <Col xs={2} md={1}><Text type="secondary">{idx + 1}.</Text></Col>
                  <Col xs={22} md={8}>
                    <Form.Item name={[field.name, 'description']} rules={[{ required: true, message: 'Required' }]}>
                      <Input placeholder="Melted Gold Jwellery" />
                    </Form.Item>
                  </Col>
                  <Col xs={8} md={4}>
                    <Form.Item name={[field.name, 'itemCount']}>
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="Nos." />
                    </Form.Item>
                  </Col>
                  <Col xs={8} md={5}>
                    <Form.Item name={[field.name, 'grossWeight']} rules={[{ required: true, message: 'Required' }]}>
                      <InputNumber min={0} precision={3} style={{ width: '100%' }} placeholder="Gross wt (g)" onChange={syncTotalWeight} />
                    </Form.Item>
                  </Col>
                  <Col xs={7} md={5}>
                    <Form.Item name={[field.name, 'remarks']}>
                      <Input placeholder="Remarks" />
                    </Form.Item>
                  </Col>
                  <Col xs={1} md={1}>
                    {fields.length > 1 && (
                      <Button type="text" danger icon={<DeleteOutlined />}
                        onClick={() => { remove(field.name); setTimeout(syncTotalWeight, 0); }} />
                    )}
                  </Col>
                </Row>
              ))}
              <Row gutter={8} align="middle" style={{ marginBottom: 12 }}>
                <Col xs={24} md={14}>
                  <Button type="dashed" block icon={<PlusOutlined />}
                    onClick={() => add(emptyItem(form.getFieldValue('metalType')))}>
                    Add Line
                  </Button>
                </Col>
                <Col xs={24} md={10}>
                  <Form.Item
                    label="Total Weight (g)" name="totalWeight" style={{ marginBottom: 0 }}
                    tooltip="Sum of the lines above. Override it if the memo must state a different figure."
                  >
                    <InputNumber min={0} precision={3} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form.List>

        <Divider orientation="left" style={{ margin: '4px 0 12px', fontSize: 13 }}>Wording</Divider>

        <Form.Item label="Purpose of Transit" name="purpose" rules={[{ required: true, message: 'Required' }]}>
          <TextArea rows={3} onChange={() => setTextTouched((t) => ({ ...t, purpose: true }))} />
        </Form.Item>
        <Form.Item label="Declaration" name="declaration" rules={[{ required: true, message: 'Required' }]}>
          <TextArea rows={4} onChange={() => setTextTouched((t) => ({ ...t, declaration: true }))} />
        </Form.Item>
        <Form.Item label="Additional Note (optional)" name="notes">
          <TextArea rows={2} placeholder="Anything else to print above the signatures" />
        </Form.Item>

        <Divider orientation="left" style={{ margin: '4px 0 12px', fontSize: 13 }}>Director Details</Divider>

        <Form.List name="directors">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <Row key={field.key} gutter={8} align="middle" style={{ marginBottom: 4 }}>
                  <Col xs={24} md={8}>
                    <Form.Item name={[field.name, 'name']}>
                      <Input placeholder="Director name" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item name={[field.name, 'din']}>
                      <Input placeholder="DIN" />
                    </Form.Item>
                  </Col>
                  <Col xs={11} md={9}>
                    <Form.Item name={[field.name, 'designation']}>
                      <Input placeholder="Authorized Director" />
                    </Form.Item>
                  </Col>
                  <Col xs={1} md={1}>
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                  </Col>
                </Row>
              ))}
              <Button type="dashed" block icon={<PlusOutlined />}
                onClick={() => add({ name: '', din: '', designation: 'Authorized Director' })}>
                Add Director
              </Button>
            </>
          )}
        </Form.List>

        <Space style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            The memo number is only consumed when you save — “Preview PDF” leaves the series untouched.
          </Text>
        </Space>
      </Form>
    </Modal>
  );
}
